import { MoreVerticalIcon } from "@moznsystems/mozn-ui";
import {
	type CSSProperties,
	type FC,
	Fragment,
	type MouseEvent,
	type ReactNode,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { useClickAway } from "../../hooks";
import { getPortalRoot } from "../../utils/portal";

export type RowAction = {
	label: string;
	icon?: ReactNode;
	onClick: () => void;
};

type RowActionsMenuProps = {
	actions: RowAction[];
	/** Accessible name for the ⋮ trigger, e.g. "Actions for customer_first_seen_at". */
	triggerLabel: string;
};

// Same layer as the popups positioned by `usePopupPosition`.
const MENU_Z_INDEX = 9999;

// `.table__row__options` pins itself to its container's inline end, so a
// zero-size container at the trigger's bottom-right drops the menu below it.
const getMenuPosition = (trigger: HTMLElement): CSSProperties => {
	const { bottom, right } = trigger.getBoundingClientRect();

	return {
		position: "absolute",
		top: bottom + window.scrollY,
		left: right + window.scrollX,
		zIndex: MENU_Z_INDEX,
	};
};

/**
 * ⋮ button that opens a list of row actions. The menu renders into
 * `#root-portal` so a table's `overflow: hidden` cannot clip it, and it closes
 * on an action, an outside click, or Escape.
 */
const RowActionsMenu: FC<RowActionsMenuProps> = ({ actions, triggerLabel }) => {
	const [menuPosition, setMenuPosition] = useState<CSSProperties | null>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const closeMenu = () => setMenuPosition(null);

	const menuRef = useClickAway<HTMLDivElement>((event) => {
		// The trigger toggles the menu itself; closing here as well would reopen it.
		if (triggerRef.current?.contains(event.target as Node)) {
			return;
		}
		closeMenu();
	});

	const onTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
		// A clickable table row must not also handle the click.
		event.stopPropagation();
		setMenuPosition(menuPosition ? null : getMenuPosition(event.currentTarget));
	};

	const onActionClick = (action: RowAction) => {
		closeMenu();
		action.onClick();
	};

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				aria-label={triggerLabel}
				aria-haspopup="menu"
				aria-expanded={menuPosition !== null}
				onClick={onTriggerClick}
				className="flex cursor-pointer items-center border-0 bg-transparent p-0"
			>
				<MoreVerticalIcon color="var(--c-gray-3)" />
			</button>
			{menuPosition
				? createPortal(
						// React bubbles events from a portal up the component tree, i.e. to the row.
						<div
							style={menuPosition}
							onClick={(event) => event.stopPropagation()}
						>
							<div ref={menuRef} role="menu" className="table__row__options">
								{actions.map((action, index) => (
									<Fragment key={action.label}>
										{index > 0 ? (
											<div
												role="separator"
												className="table__row__options__separator"
											/>
										) : null}
										<button
											type="button"
											role="menuitem"
											onClick={() => onActionClick(action)}
											className="table__row__options__option w-full border-0 bg-transparent text-start [color:inherit] [font-family:inherit] [font-size:inherit]"
										>
											{action.icon}
											{action.label}
										</button>
									</Fragment>
								))}
							</div>
						</div>,
						getPortalRoot(),
					)
				: null}
		</>
	);
};

export { RowActionsMenu };
